// redux/store.js
import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { backendApi, middleware as backendApiMiddleware } from '../common/backend-api/backend-api';
import { AppThunkDispatch } from '../common/types/common-root-state-type';
import aiMessagesReducer from './slices/aiMessagesSlice';
import ChatReducer from './slices/chatSlice';
import legalQueryProgressReducer from './slices/legalQueryProgressSlice';
import mqttReducer from './slices/mqttSlice';
import PracticeAreaReducer from './slices/practiceAreaSlice';
import simpleLegalProgressReducer from './slices/simpleLegalProgressSlice'; // ✅ New simplified slice
import UIReducer from './slices/uiSlice';
import UserReducer from './slices/userSlice';
// DELETED: import courtResearchProgressReducer from './slices/courtResearchProgressSlice';
import CaseReducer from './slices/caseSlice';
import documentSearchReducer from './slices/documentSearchSlice';
import organizationReducer from './slices/organizationSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import userDocumentSearchReducer from './slices/userDocumentSearchSlice';
import legalNewsReducer from './slices/legalNewsSlice';
import {
  DBAIAgentMetricsReducer,
  DBCaseDocumentsSearchReducer,
  DBCaseReducer,
  DBCaseUserReducer,
  DBChatReducer,
  DBDocumentReducer,
  // DELETED: DBCitationReducer,
  DBEntryReducer,
  DBKnowledgebaseReducer,
  DBMessageReducer,
  DBTimelineEventReducer,
  // DELETED: DBCourtResearchResultReducer,
  DBTimelineReducer,
  DBUserReducer
} from './thunks/DBServiceThunks';
import { RESET_STATE } from './thunks/UserThunks';

// Define the reducer map
const reducerMap = {
  ChatReducer,
  PracticeAreaReducer,
  UIReducer,
  UserReducer,
  CaseReducer,
  aiMessages: aiMessagesReducer,
  mqtt: mqttReducer,
  legalQueryProgress: legalQueryProgressReducer, // Keep old for compatibility
  simpleLegalProgress: simpleLegalProgressReducer, // ✅ New simplified progress
  // DELETED: courtResearchProgress: courtResearchProgressReducer,
  subscription: subscriptionReducer,
  documentSearch: documentSearchReducer,
  userDocumentSearch: userDocumentSearchReducer,
  organization: organizationReducer,
  legalNews: legalNewsReducer,
  DBChatReducer,
  DBCaseReducer,
  DBKnowledgebaseReducer,
  DBDocumentReducer,
  DBEntryReducer,
  DBUserReducer,
  DBMessageReducer,
  // DELETED: DBCitationReducer,
  DBAIAgentMetricsReducer,
  // DELETED: DBCourtResearchResultReducer,
  DBTimelineReducer,
  DBTimelineEventReducer,
  DBCaseUserReducer,
  DBCaseDocumentsSearchReducer,
  // Add the backendApi reducer
  [backendApi.reducerPath]: backendApi.reducer,
}

// Create the root reducer
const appReducer = combineReducers(reducerMap);

// Root reducer with state reset capability
const rootReducer = (state: any, action: AnyAction) => {
  // If the action is RESET_STATE, reset all state to initial values
  if (action.type === RESET_STATE) {
    // We need to preserve backendApi state to avoid errors with RTK Query
    const backendApiState = state?.[backendApi.reducerPath];

    // Reset the state
    state = undefined;

    // Create new state with API state preserved and ensure UserReducer is properly initialized
    return appReducer({
      [backendApi.reducerPath]: backendApiState,
      // Preserve critical UserReducer state for proper auth flow
      UserReducer: {
        ...UserReducer(undefined, action),
        loading: false,
        initialized: true,
        isAuthenticated: false
      }
    }, action);
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  // Add the middleware to the store
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(backendApiMiddleware),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export default store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Redux hooks
export const useAppDispatch = () => useDispatch<AppThunkDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;